import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AskTeamAssist from 'actions/team_assist/ask_team_assist';
import ClearTeamAssist from 'actions/team_assist/clear_team_assist';
import ShowToast from 'actions/toast_deprecated/show_toast';
import ToggleTeamAssistPanel from 'actions/team_assist/toggle_team_assist_panel';
import connect from 'components/lib/connect';
import TeamAssistIcon from 'components/lib/icons/team_assist_icon';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import MessageBubble, { AssistantRow } from 'components/customer/team_assist/team_assist_message';
import TeamAssistEmptyStateIcon from 'components/customer/team_assist/team_assist_empty_state_icon';
import TeamAssistModal from 'components/customer/team_assist/team_assist_modal';
import TeamAssistMessageModel from 'models/team_assist_message';
import { ToastType } from 'models/toast_deprecated';
import {
  BetaTag,
  CannedResponseButton,
  CannedResponseButtons,
  TeamAssistActionsDropdown,
  TeamAssistActionsMenuIcon,
  TeamAssistActionsMenuOpener,
  TeamAssistInput,
  TeamAssistPanelActionsMenu,
} from 'components/customer/team_assist/team_assist_panel_shared';

function TeamAssistPanel({ cannedResponses, customerId, customerName, executionsByMessageId, isLoading, messages }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isDemoMode = isFeatureEnabled('demoMode');
  const [inputValue, setInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const messagesAreaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const prevLoadingRef = useRef(isLoading);
  const hasScrolledSinceMountRef = useRef(false);
  const isEmpty = messages.length === 0 && !isLoading;
  // Openers like "Catch me up" stop being relevant once the agent is mid-chat,
  // so only responses flagged alwaysShow survive past the first message. Keyed
  // on the message count rather than isEmpty: opening a customer sets the
  // TeamAssist store loading before any fetch resolves, and treating that
  // window as "mid-chat" would flash the alwaysShow subset on every open and
  // then pop the openers in once the messages arrived.
  const visibleCannedResponses =
    messages.length > 0 ? cannedResponses.filter(cr => cr.alwaysShow) : isLoading ? [] : cannedResponses;
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;
  const sessionId = messages.length ? messages[0].sessionId : null;

  const menuOptions = [
    { label: 'Clear session', value: 'clear-session' },
    ...(sessionId ? [{ label: 'Copy link to session', value: 'copy-session-link' }] : []),
  ];

  useEffect(() => {
    if (!messagesAreaRef.current) {
      return;
    }

    const justFinishedLoading = prevLoadingRef.current && !isLoading;
    prevLoadingRef.current = isLoading;
    const isInitialOrReturning = !hasScrolledSinceMountRef.current;
    if (isInitialOrReturning) {
      hasScrolledSinceMountRef.current = true;
    }
    const behavior = justFinishedLoading || isInitialOrReturning ? 'auto' : 'smooth';
    const container = messagesAreaRef.current;
    if (behavior === 'smooth' && container.scrollTo) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [isLoading, lastMessageId]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isModalOpen]);

  const handleSubmit = useCallback(() => {
    const question = inputValue.trim();
    if (!question || isLoading) {
      return;
    }

    setInputValue('');
    executeAction(AskTeamAssist, { customerId, question });
  }, [customerId, executeAction, inputValue, isLoading]);

  const handleCannedResponse = useCallback(
    cannedResponse => {
      if (isLoading) {
        return;
      }
      executeAction(AskTeamAssist, {
        customerId,
        question: cannedResponse.message,
        displayedMessage: cannedResponse.label,
      });
    },
    [customerId, executeAction, isLoading]
  );

  const handleClear = useCallback(() => {
    if (!messages.length) {
      return;
    }
    executeAction(ClearTeamAssist, { customerId });
  }, [customerId, executeAction, messages.length]);

  const handleCopySessionLink = useCallback(() => {
    if (!sessionId || !navigator.clipboard) {
      return;
    }
    const url = `${window.location.origin}/admin/team-assist/sessions/${sessionId}`;
    navigator.clipboard.writeText(url).then(
      () => {
        executeAction(ShowToast, { type: ToastType.INFO, message: 'Link copied to clipboard' });
      },
      () => {
        executeAction(ShowToast, { type: ToastType.ERROR, message: 'Failed to copy link' });
      }
    );
  }, [executeAction, sessionId]);

  const onSelectAction = useCallback(
    value => {
      if (value === 'copy-session-link') {
        handleCopySessionLink();
      } else if (value === 'clear-session') {
        handleClear();
      }
    },
    [handleClear, handleCopySessionLink]
  );

  const onKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      } else if (evt.key === 'Escape') {
        executeAction(ToggleTeamAssistPanel);
      }
    },
    [executeAction, handleSubmit]
  );

  const onModalKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      } else if (evt.key === 'Escape') {
        evt.preventDefault();
        setIsModalOpen(false);
      }
    },
    [handleSubmit]
  );

  const onInputChange = useCallback(evt => {
    setInputValue(evt.target.value);
  }, []);

  return (
    <>
      <StyledPanel data-aid="teamAssistPanel" tabIndex="0">
        <Header>
          <StyledHeaderIcon height="16" width="16" />
          <HeaderTextWrap>
            <HeaderText>
              {t('Team Assist')} {!isDemoMode && <BetaTag>{t('Beta')}</BetaTag>}
            </HeaderText>
            <HeaderSubtitle>{customerName || 'this customer'}</HeaderSubtitle>
          </HeaderTextWrap>
          <HeaderActions>
            {messages.length > 0 && (
              <TeamAssistActionsDropdown
                data-aid="teamAssistActionsMenu"
                menuComponent={TeamAssistPanelActionsMenu}
                onSelect={onSelectAction}
                openerComponent={TeamAssistActionsMenuOpener}
                options={menuOptions}
                position="bottom"
                text={<TeamAssistActionsMenuIcon />}
                value=""
              />
            )}
            <ExpandButton
              data-aid="teamAssistExpandButton"
              onClick={() => setIsModalOpen(true)}
              title="Expand"
              type="button"
            >
              <i className="fa fa-expand" />
            </ExpandButton>
          </HeaderActions>
        </Header>
        <MessagesArea ref={messagesAreaRef}>
          {isEmpty && (
            <EmptyState>
              <EmptyStateIconWrapper>
                <TeamAssistEmptyStateIcon size={100} />
              </EmptyStateIconWrapper>
              <EmptyStateText>{t('Ask anything about this customer—answers, history, or next steps.')}</EmptyStateText>
            </EmptyState>
          )}
          {messages.map((msg, idx) => (
            <MessageBubble
              execution={
                msg.type === TeamAssistMessageModel.Type.proposed_action ? executionsByMessageId[msg.id] : undefined
              }
              isLastMessage={idx === messages.length - 1}
              key={msg.id}
              msg={msg}
            />
          ))}
          {isLoading && (
            <AssistantRow>
              <LoadingDots>
                <Dot style={{ animationDelay: '0s' }} />
                <Dot style={{ animationDelay: '0.2s' }} />
                <Dot style={{ animationDelay: '0.4s' }} />
              </LoadingDots>
            </AssistantRow>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>
        <InputArea>
          {visibleCannedResponses.length > 0 && (
            <CannedResponseButtons>
              {visibleCannedResponses.map((cr, idx) => (
                <CannedResponseButton
                  data-aid="teamAssistCannedResponseButton"
                  disabled={isLoading}
                  key={idx}
                  onClick={() => handleCannedResponse(cr)}
                  type="button"
                >
                  {cr.label}
                </CannedResponseButton>
              ))}
            </CannedResponseButtons>
          )}
          <TeamAssistInput
            dataAid="teamAssistInput"
            dataAidSend="teamAssistSendButton"
            disabled={!inputValue.trim() || isLoading}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onSubmit={handleSubmit}
            value={inputValue}
          />
        </InputArea>
      </StyledPanel>
      {isModalOpen && (
        <TeamAssistModal
          cannedResponses={visibleCannedResponses}
          customerName={customerName}
          executionsByMessageId={executionsByMessageId}
          inputValue={inputValue}
          isLoading={isLoading}
          menuOptions={menuOptions}
          messages={messages}
          messagesEndRef={messagesEndRef}
          onCannedResponse={handleCannedResponse}
          onClose={() => setIsModalOpen(false)}
          onInputChange={onInputChange}
          onKeyDown={onModalKeyDown}
          onSelectAction={onSelectAction}
          onSubmit={handleSubmit}
          onUse={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

TeamAssistPanel.propTypes = {
  cannedResponses: PropTypes.arrayOf(
    PropTypes.shape({
      alwaysShow: PropTypes.bool,
      label: PropTypes.string,
      message: PropTypes.string,
    })
  ),
  customerId: PropTypes.string,
  customerName: PropTypes.string,
  executionsByMessageId: PropTypes.object,
  isLoading: PropTypes.bool,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.string,
      role: PropTypes.string,
      sessionId: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

TeamAssistPanel.defaultProps = {
  cannedResponses: [],
  executionsByMessageId: {},
  isLoading: false,
  messages: [],
};

function mapStateToProps({ getProvider }) {
  const customerId = getProvider('currentLocation').get().customerId;
  const profile = getProvider('profile').get();
  const teamAssistsProvider = getProvider('teamAssists');
  const allMessages = teamAssistsProvider.findAll() || [];
  const isLoading = teamAssistsProvider.isLoading();
  const teamAssistConfig = getProvider('teamAssistConfiguration').get();
  const cannedResponses = (teamAssistConfig && teamAssistConfig.cannedResponses) || [];

  // Walk the message stream once to derive (a) the visible bubble list, with
  // server-bookkeeping `follow_up_trigger` entries filtered out, and (b) a
  // lookup from a proposed-action message's id to the outcome reported by
  // the follow-up trigger that ran it. The proposed-action card uses (b) to
  // render its executed / error state on reload without persisting any
  // client-side flag.
  const executionsByMessageId = {};
  const messages = [];
  for (const msg of allMessages) {
    if (msg.type === TeamAssistMessageModel.Type.follow_up_trigger) {
      (msg.proposedActionResults || []).forEach(r => {
        if (r && r.messageId) {
          executionsByMessageId[r.messageId] = {
            isError: Boolean(r.isError),
            result: typeof r.result === 'string' ? r.result : '',
          };
        }
      });
      continue;
    }
    messages.push(msg);
  }

  return {
    cannedResponses,
    customerId,
    customerName: profile && profile.name,
    executionsByMessageId,
    isLoading,
    messages,
  };
}

export default connect(mapStateToProps)(TeamAssistPanel);

const StyledPanel = styled.div`
  box-shadow: ${p => p.theme.boxShadow.small};
  display: flex;
  flex: 0 0 25%;
  flex-direction: column;
  height: 100%;
  max-width: 360px;
  min-width: 200px;
  outline: none;
  width: 25%;
  z-index: 3;
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  padding: 16px;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  font-size: var(--font-size-base);
  padding: 4px;
  &:hover {
    color: ${p => p.theme.colors.purple400};
  }
`;

const StyledHeaderIcon = styled(TeamAssistIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
`;

const HeaderText = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.purple400};
  display: flex;
  font-size: var(--font-size-medium);
  font-weight: ${p => p.theme.fontWeight.medium};
  gap: 6px;
`;

const HeaderSubtitle = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: var(--font-size-reduced);
  line-height: 1.2;
  margin-left: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 160px;
  margin-right: 2px;
  min-width: 0;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const MessagesArea = styled.div`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: none;
  padding: 16px;
`;

const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 24px 16px;
  text-align: center;
`;

const EmptyStateIconWrapper = styled.div`
  color: ${p => p.theme.colors.purple400};
  margin-bottom: 16px;
  opacity: 0.85;
`;

const EmptyStateText = styled.p`
  color: ${p => p.theme.colors.gray800};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  margin: 0;
  max-width: 260px;
`;

const InputArea = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  padding: 12px 16px;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 12px;
`;

const Dot = styled.div`
  animation: pulse 1s ease-in-out infinite;
  background: ${p => p.theme.colors.gray400};
  border-radius: 50%;
  height: 6px;
  width: 6px;

  @keyframes pulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }
`;
